import { useCallback, useEffect, useState } from 'react'
import {
  AUTO_APPROVABLE_COMMANDS,
  approvalGate,
  isCustomerApprover,
  isRole,
  isRunCommand,
  roleHasCapability,
} from '@qim/shared'
import {
  ApiError,
  approveRun,
  approveRuns,
  fetchApprovalSettings,
  fetchApprovals,
  rejectRun,
  setApprovalSettings,
} from '../api.js'
import { formatInstant } from '../format.js'
import { pathFor } from '../routing.js'
import { Modal } from './modal.jsx'
import { RunState } from './status.jsx'
import { ProtectionLock } from './lock.jsx'
import type { ApprovalSettingsWire, ApprovalsWire, DeploymentSummary, RunSummary } from '../api.js'
import type {
  AutoApprovableCommand,
  CustomerApprover,
  RunApprovalPolicy,
  TenantKind,
} from '@qim/shared'
import type { FormEvent, JSX } from 'react'

/**
 * Approvals, in the console (IM-102, IM-39).
 *
 * The customer approves what Qrvey's staff start in their account — here, in
 * the portal, once customer sign-in is on (IM-154) — and a second person
 * approves the destroy of a protected Qrvey instance. One queue for both,
 * because the act is the same: read what was asked for, then agree or refuse.
 *
 * Nothing here decides who may decide. The buttons are offered where the API
 * would accept them — a capability the role holds, and the few things a
 * capability cannot say (not your own request; not staff on a customer's run)
 * — because a button that always fails is a worse way to say no. The API
 * checks all of it regardless and records the decision either way.
 */

/** Who is looking, which is what decides which buttons would succeed. */
export interface Viewer {
  readonly role: string
  readonly email?: string
  /** Staff (federated, or break-glass). Staff cannot decide for a customer. */
  readonly isStaff?: boolean
}

const holds = (role: string, capability: 'deployment:approve' | 'approvals:manage'): boolean =>
  isRole(role) && roleHasCapability(role, capability)

const WHO_DECIDES: Readonly<Record<string, string>> = {
  customer: 'the customer',
  'second-approver': 'a second approver',
}

/** Whether this viewer's Approve would be accepted — the API's own rules, offered. */
export const mayDecide = (
  viewer: Viewer,
  run: RunSummary,
  decision: 'approve' | 'reject',
): boolean => {
  if (!holds(viewer.role, 'deployment:approve')) return false
  if (run.approval?.requirement === 'customer' && viewer.isStaff === true) return false
  const own =
    viewer.email !== undefined &&
    viewer.email.trim().toLowerCase() === run.requestedBy.trim().toLowerCase()
  return !(decision === 'approve' && own)
}

/** The sentence a refusal came back with, or a plain one if it had none. */
const refusalOf = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as { detail?: unknown; error?: unknown } | undefined
    if (typeof body?.detail === 'string') return body.detail
    if (typeof body?.error === 'string') return body.error
    if (error.status === 403) return 'Your role does not permit that.'
  }
  return 'Could not save that just now.'
}

const RunLink = ({
  tenantId,
  runId,
  onOpen,
}: {
  readonly tenantId: string
  readonly runId: string
  readonly onOpen: (path: string) => void
}): JSX.Element => {
  const href = pathFor({ screen: 'run', tenantId, runId })
  return (
    <a
      href={href}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
        event.preventDefault()
        onOpen(href)
      }}
    >
      <code>{runId}</code>
    </a>
  )
}

/**
 * Asks for an optional reason before refusing a run.
 *
 * A modal because every form here opens in one (IM-146). The reason is
 * optional: a customer refusing an apply during quarter close owes nobody an
 * essay, but "not during quarter close" saves the engineer a phone call.
 */
const RejectDialog = ({
  run,
  onConfirm,
  onCancel,
}: {
  readonly run: RunSummary
  readonly onConfirm: (reason: string) => void
  readonly onCancel: () => void
}): JSX.Element => {
  const [reason, setReason] = useState('')
  const submit = (event: FormEvent): void => {
    event.preventDefault()
    onConfirm(reason)
  }
  return (
    <Modal title={`Reject this ${run.command}?`} onCancel={onCancel}>
      <form onSubmit={submit}>
        <p>
          Nothing has been started, and nothing will be. {run.requestedBy} is told why if you say.
        </p>
        <label>
          Reason <span className="muted">(optional)</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} />
        </label>
        <div className="actions">
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit">Reject</button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * What Qrvey may run without asking. Destroy is on the list, unchecked and
 * disabled, with the reason — so the customer can see it was considered rather
 * than forgotten.
 */
const Settings = ({
  data,
  canChange,
  onSave,
}: {
  readonly data: ApprovalsWire
  readonly canChange: boolean
  readonly onSave: (autoApprove: readonly string[]) => Promise<void>
}): JSX.Element => {
  const [chosen, setChosen] = useState<readonly string[]>(data.autoApprove)
  const [busy, setBusy] = useState(false)
  useEffect(() => setChosen(data.autoApprove), [data.autoApprove])

  const toggle = (command: string): void =>
    setChosen(chosen.includes(command) ? chosen.filter((c) => c !== command) : [...chosen, command])

  const changed =
    chosen.length !== data.autoApprove.length || chosen.some((c) => !data.autoApprove.includes(c))

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        setBusy(true)
        void onSave(chosen).finally(() => setBusy(false))
      }}
    >
      <h3>Run without asking</h3>
      <p className="hint">
        What Qrvey may start in this customer’s account without waiting for their approval. A run
        the customer starts themselves never waits.
      </p>
      <ul className="approval-settings">
        {AUTO_APPROVABLE_COMMANDS.map((command) => (
          <li key={command}>
            <input
              type="checkbox"
              id={`auto-${command}`}
              checked={chosen.includes(command)}
              disabled={!canChange || busy}
              onChange={() => toggle(command)}
            />
            <label htmlFor={`auto-${command}`}>{command}</label>
          </li>
        ))}
        <li>
          <input type="checkbox" id="auto-destroy" checked={false} disabled readOnly />
          <label htmlFor="auto-destroy">
            {data.neverAutoApproved.command}{' '}
            <span className="muted">— never: {data.neverAutoApproved.reason}</span>
          </label>
        </li>
      </ul>
      <p className="muted">
        {data.chosen === null
          ? `The default: ${data.defaultAutoApprove.join(' and ')}, which change nothing. Nobody has chosen otherwise.`
          : `Chosen by ${data.chosen.by} on ${formatInstant(data.chosen.at)}.`}
      </p>
      {canChange ? (
        <div className="actions">
          <button type="submit" disabled={!changed || busy}>
            Save
          </button>
        </div>
      ) : (
        <p className="hint">Only the customer’s admin can change this.</p>
      )}
    </form>
  )
}

export const ApprovalsPanel = ({
  baseUrl,
  tenantId,
  viewer,
  onOpen,
  onChanged,
  standalone = false,
  kind,
}: {
  readonly baseUrl: string
  readonly tenantId: string
  readonly viewer: Viewer
  /** The customer's kind, for the lock beside each instance (IM-174). */
  readonly kind?: TenantKind | undefined
  readonly onOpen: (path: string) => void
  /** Reloads the screen around it, so an approved run appears as started. */
  readonly onChanged: () => void
  /**
   * Rendered as a tab of its own (the customer screen's Approvals tab), where
   * an empty panel would read as a broken page. As a section among others it
   * stayed silent when there was nothing to say; as the whole page it says so.
   */
  readonly standalone?: boolean
}): JSX.Element | null => {
  const [data, setData] = useState<ApprovalsWire | undefined>(undefined)
  const [readFailure, setReadFailure] = useState<string | undefined>(undefined)
  const [failure, setFailure] = useState<string | undefined>(undefined)
  const [note, setNote] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<readonly string[]>([])
  const [rejecting, setRejecting] = useState<RunSummary | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await fetchApprovals(baseUrl, tenantId))
      setReadFailure(undefined)
    } catch {
      setReadFailure('Could not read this customer’s approvals just now.')
    }
  }, [baseUrl, tenantId])

  useEffect(() => {
    void load()
  }, [load])

  const after = async (work: () => Promise<string>): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    setNote(undefined)
    try {
      setNote(await work())
      setSelected([])
      await load()
      onChanged()
    } catch (error: unknown) {
      setFailure(refusalOf(error))
    } finally {
      setBusy(false)
    }
  }

  if (data === undefined) {
    if (readFailure === undefined && standalone) return <p role="status">Reading…</p>
    return readFailure === undefined ? null : (
      <section className="panel">
        <h2>Approvals</h2>
        <p className="muted">{readFailure}</p>
      </section>
    )
  }

  // Qrvey's own tenants have nothing to configure; the panel appears only when
  // a protected destroy is waiting, or has been decided recently.
  const isCustomer = data.policy === 'customer'
  if (!isCustomer && data.pending.length === 0 && data.decided.length === 0) {
    return standalone ? (
      <section className="panel">
        <h2>Approvals</h2>
        <p role="status">
          Nothing is waiting. In Qrvey&rsquo;s own customers only the destroy of a protected
          instance asks a second person first.
        </p>
      </section>
    ) : null
  }

  const approvable = data.pending
    .map((item) => item.run)
    .filter((run) => mayDecide(viewer, run, 'approve'))

  return (
    <section className="panel">
      <h2>
        Approvals <span className="muted">· {data.pending.length} waiting</span>
      </h2>

      {/* Said where the customer looks for their approvals (IM-176): with
        nobody to ask, a run that would have waited goes ahead, and the tab
        that lists what waits should not imply otherwise. */}
      {isCustomer && approverIn(data) !== 'available' ? (
        <p className="hint approval-notice">
          {approverIn(data) === 'sign-in-off'
            ? 'Customer sign-in is not on yet, so nobody at this customer can approve. '
            : 'This customer has nobody who can approve yet. '}
          Runs Qrvey starts here go ahead without waiting; approval will be asked for once they have
          an admin who can sign in.
        </p>
      ) : null}

      {data.pending.length === 0 ? (
        <p role="status">Nothing is waiting for approval.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col" className="approval-select">
                  <span className="visually-hidden">Select</span>
                </th>
                <th scope="col">Run</th>
                <th scope="col">Instance</th>
                <th scope="col">Asked by</th>
                <th scope="col">Needs</th>
                <th scope="col">What it would run</th>
                <th scope="col">
                  <span className="visually-hidden">Decide</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.pending.map(({ run, latestPlan, protected: mark }) => (
                <tr key={run.runId}>
                  <td className="approval-select">
                    {mayDecide(viewer, run, 'approve') ? (
                      <input
                        type="checkbox"
                        aria-label={`Select ${run.runId}`}
                        checked={selected.includes(run.runId)}
                        onChange={() =>
                          setSelected(
                            selected.includes(run.runId)
                              ? selected.filter((id) => id !== run.runId)
                              : [...selected, run.runId],
                          )
                        }
                      />
                    ) : null}
                  </td>
                  <td>
                    <RunLink tenantId={tenantId} runId={run.runId} onOpen={onOpen} />{' '}
                    <strong>{run.command}</strong>
                    <p>
                      <RunState state={run.state} />
                    </p>
                  </td>
                  <td>
                    {run.deploymentId ?? '—'}
                    <ProtectionLock mark={mark} kind={kind} />
                    <p className="muted">{run.accountKey}</p>
                  </td>
                  <td>
                    {run.requestedBy}
                    <p className="muted">
                      <time dateTime={run.requestedAt}>{formatInstant(run.requestedAt)}</time>
                    </p>
                  </td>
                  <td>
                    {WHO_DECIDES[run.approval?.requirement ?? ''] ?? run.approval?.requirement}
                    {run.approval?.expiresAt === undefined ? null : (
                      <p className="muted">
                        expires{' '}
                        <time dateTime={run.approval.expiresAt}>
                          {formatInstant(run.approval.expiresAt)}
                        </time>
                      </p>
                    )}
                  </td>
                  <td>
                    {/* The same document the requester sent, secrets removed —
                      IM-39's "the approver sees what the requester saw". The
                      plan's log is what says what would change; a rendered
                      diff is IM-35. */}
                    {run.variables === undefined || Object.keys(run.variables).length === 0 ? (
                      <span className="muted">No variables</span>
                    ) : (
                      <details>
                        <summary>
                          {Object.keys(run.variables).length === 1
                            ? '1 variable'
                            : `${String(Object.keys(run.variables).length)} variables`}
                        </summary>
                        <pre>{JSON.stringify(run.variables, null, 2)}</pre>
                      </details>
                    )}
                    {latestPlan === undefined ? (
                      <p className="muted">No plan has been run against this instance.</p>
                    ) : (
                      <p className="muted">
                        Latest plan:{' '}
                        <RunLink tenantId={tenantId} runId={latestPlan.runId} onOpen={onOpen} /> (
                        {latestPlan.state}, {formatInstant(latestPlan.requestedAt)}) — its log shows
                        what would change.
                      </p>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      {mayDecide(viewer, run, 'approve') ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() =>
                            void after(async () => {
                              await approveRun(baseUrl, tenantId, run.runId)
                              return `Approved ${run.runId}. It has been started.`
                            })
                          }
                        >
                          Approve
                        </button>
                      ) : null}
                      {mayDecide(viewer, run, 'reject') ? (
                        <button
                          type="button"
                          className="ghost"
                          disabled={busy}
                          onClick={() => setRejecting(run)}
                        >
                          Reject
                        </button>
                      ) : null}
                      {!mayDecide(viewer, run, 'approve') && !mayDecide(viewer, run, 'reject') ? (
                        <span className="muted">
                          Waiting for {WHO_DECIDES[run.approval?.requirement ?? ''] ?? 'approval'}
                        </span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approvable.length > 1 ? (
        <div className="actions">
          <button
            type="button"
            disabled={busy || selected.length === 0}
            onClick={() =>
              void after(async () => {
                const { results } = await approveRuns(baseUrl, tenantId, selected)
                const refused = results.filter((r) => r.status >= 300)
                const started = results.length - refused.length
                return refused.length === 0
                  ? `Approved ${String(started)} runs. They have been started.`
                  : `Approved ${String(started)}; ${refused
                      .map((r) => `${r.runId}: ${r.detail ?? r.error ?? String(r.status)}`)
                      .join('; ')}`
              })
            }
          >
            Approve selected ({selected.length})
          </button>
        </div>
      ) : null}

      {note === undefined ? null : <p role="status">{note}</p>}
      {failure === undefined ? null : <p className="failure">{failure}</p>}

      {data.decided.length === 0 ? null : (
        <details>
          <summary>Recently decided ({data.decided.length})</summary>
          <ul>
            {data.decided.map((run) => (
              <li key={run.runId}>
                <RunLink tenantId={tenantId} runId={run.runId} onOpen={onOpen} /> {run.command} by{' '}
                {run.requestedBy} — {run.approval?.decision?.outcome ?? run.state}
                {run.approval?.decision === undefined
                  ? ''
                  : ` by ${run.approval.decision.by} on ${formatInstant(run.approval.decision.at)}`}
                {run.approval?.decision?.reason === undefined
                  ? ''
                  : ` — “${run.approval.decision.reason}”`}
              </li>
            ))}
          </ul>
        </details>
      )}

      {isCustomer ? (
        <Settings
          data={data}
          canChange={holds(viewer.role, 'approvals:manage') && viewer.isStaff !== true}
          onSave={(autoApprove) =>
            after(async () => {
              await setApprovalSettings(baseUrl, tenantId, autoApprove)
              return 'Saved.'
            })
          }
        />
      ) : null}

      {rejecting === undefined ? null : (
        <RejectDialog
          run={rejecting}
          onCancel={() => setRejecting(undefined)}
          onConfirm={(reason) => {
            const run = rejecting
            setRejecting(undefined)
            void after(async () => {
              await rejectRun(baseUrl, tenantId, run.runId, reason)
              return `Rejected ${run.runId}. Nothing was started.`
            })
          }}
        />
      )}
    </section>
  )
}

/** A run against this instance waits for somebody to agree (IM-102, IM-39). */
export const AwaitingHint = ({
  deployment,
}: {
  readonly deployment: Pick<DeploymentSummary, 'awaitingApproval'>
}): JSX.Element | null => {
  const waiting = deployment.awaitingApproval
  if (waiting === undefined) return null
  return (
    <p className="muted">
      <RunState state="awaiting-approval" /> {waiting.command} by {waiting.requestedBy}, waiting for{' '}
      {WHO_DECIDES[waiting.requirement] ?? waiting.requirement}
    </p>
  )
}

/**
 * A tenant's approval rule, read when a run form opens (IM-102).
 *
 * One DynamoDB read in Qrvey's own account, not a call into a customer's, and
 * only when the form is on screen — the form mounts when its modal opens. A
 * failed read leaves it undefined, and the form then says nothing about
 * approval rather than guessing: the API decides either way, and the run's own
 * state says what it decided.
 */
export const useApprovalFacts = (
  baseUrl: string,
  tenantId: string,
): ApprovalSettingsWire | undefined => {
  const [facts, setFacts] = useState<ApprovalSettingsWire | undefined>(undefined)
  useEffect(() => {
    let live = true
    fetchApprovalSettings(baseUrl, tenantId)
      .then((answer) => {
        if (live) setFacts(answer)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [baseUrl, tenantId])
  return facts
}

const isPolicy = (value: string): value is RunApprovalPolicy =>
  value === 'customer' || value === 'protected-destroys'

/** Why nobody at the customer can approve yet, as a clause (IM-176). */
const NOBODY_CAN_APPROVE: Readonly<Record<string, string>> = {
  'none-assigned': 'this customer has nobody who can approve yet',
  'sign-in-off': 'customer sign-in is not on yet, so nobody at the customer can approve',
}

/** The customer's approver state, reading an absent or unknown value as the old behaviour. */
const approverIn = (facts: ApprovalSettingsWire): CustomerApprover =>
  isCustomerApprover(facts.customerApprover) ? facts.customerApprover : 'available'

/**
 * What the run form says before a command is submitted: whether it will wait,
 * and for whom — or that it would have, and will go ahead because nobody at
 * the customer can approve (IM-176). The API's own gate, asked in advance with
 * the same facts.
 */
export const approvalNotice = (
  facts: ApprovalSettingsWire | undefined,
  command: string,
  requester: { readonly isStaff: boolean; readonly protectedInstance: boolean },
): { readonly waits: boolean; readonly notice: string } | undefined => {
  if (facts === undefined || !isPolicy(facts.policy) || !isRunCommand(command)) return undefined
  const autoApprove = facts.autoApprove.filter((c): c is AutoApprovableCommand =>
    (AUTO_APPROVABLE_COMMANDS as readonly string[]).includes(c),
  )
  const answer = approvalGate({
    policy: facts.policy,
    command,
    requestedByCustomer: !requester.isStaff,
    settings: { autoApprove, at: '', by: '' },
    protectedInstance: requester.protectedInstance,
    customerApprover: approverIn(facts),
  })
  if (answer.outcome === 'no-approver') {
    return {
      waits: false,
      notice: `will go ahead without the customer’s approval: ${NOBODY_CAN_APPROVE[approverIn(facts)] ?? 'nobody can approve yet'}`,
    }
  }
  if (answer.outcome !== 'pending') return undefined
  return {
    waits: true,
    notice:
      answer.requirement === 'customer'
        ? 'will need the customer’s approval'
        : 'will need a second approver',
  }
}
